import type { ProductSummary } from '@aeris/contracts'

import { fetchProducts } from './api/catalog.ts'
import { fetchSalesDocuments } from './api/sales.ts'

/**
 * O que um código bipado é — F206.
 *
 * O cupom leva o NÚMERO da venda (com zeros à esquerda desde 27/09: `000074`);
 * a etiqueta leva o código ou o código de barras do produto. Um número curto é
 * provavelmente cupom; um longo, produto (um EAN tem 13 dígitos). Cada um tenta
 * primeiro o provável e depois o outro, para uma loja que etiqueta produto com
 * código "1234" não ficar sem resposta.
 *
 * Venda é pelo número EXATO: a busca acha "16" em 116 e 160, e bipar 16 não
 * pode abrir a 160. Duas filiais com o mesmo número: vale a desta filial.
 */

export type ScanResult =
  | { readonly kind: 'sale'; readonly documentId: string }
  | {
      readonly kind: 'product'
      readonly product: ProductSummary
      readonly variantId: string
    }
  | { readonly kind: 'none'; readonly code: string }

const SHORT_NUMBER = /^\d{1,8}$/

const findSale = async (
  code: string,
  branchId: string | undefined
): Promise<string | null> => {
  const number = code.replace(/^0+(?=\d)/, '')
  if (!/^\d+$/.test(number)) return null
  const found = await fetchSalesDocuments({
    search: number,
    page: 1,
    pageSize: 10,
  })
  const exact = found.items.filter((item) => String(item.number) === number)
  const here = exact.filter(
    (item) => branchId === undefined || item.branchId === branchId
  )
  const chosen = here.length === 1 ? here : exact
  return chosen.length === 1 ? (chosen[0]?.id ?? null) : null
}

const findProduct = async (
  code: string,
  branchId: string | undefined
): Promise<{ product: ProductSummary; variantId: string } | null> => {
  const found = await fetchProducts({
    search: code,
    status: 'active',
    page: 1,
    pageSize: 10,
    include: branchId === undefined ? ['price'] : ['price', 'stock'],
    ...(branchId === undefined ? {} : { branchId }),
  })
  const wanted = code.trim().toLowerCase()
  for (const product of found.items) {
    const variant = product.variants.find(
      (entry) =>
        entry.sku.toLowerCase() === wanted ||
        entry.gtin?.toLowerCase() === wanted
    )
    if (variant !== undefined) return { product, variantId: variant.id }
  }
  return null
}

export const resolveScan = async (
  code: string,
  options: {
    readonly branchId: string | undefined
    readonly canReadSales: boolean
    readonly canReadCatalog: boolean
  }
): Promise<ScanResult> => {
  const trySale = async (): Promise<ScanResult | null> => {
    if (!options.canReadSales) return null
    const id = await findSale(code, options.branchId)
    return id === null ? null : { kind: 'sale', documentId: id }
  }
  const tryProduct = async (): Promise<ScanResult | null> => {
    if (!options.canReadCatalog) return null
    const hit = await findProduct(code, options.branchId)
    return hit === null ? null : { kind: 'product', ...hit }
  }

  const order = SHORT_NUMBER.test(code)
    ? [trySale, tryProduct]
    : [tryProduct, trySale]
  for (const attempt of order) {
    const result = await attempt()
    if (result !== null) return result
  }
  return { kind: 'none', code }
}
